import React from 'react';
import { motion } from 'framer-motion';
import { useSEO, seoConfigs } from '../hooks/useSEO';

export const MusicPage: React.FC = () => {
  useSEO(seoConfigs.music);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Background Effects */}
      <div className="fixed inset-0 z-0">
        <div className="absolute -top-32 left-1/2 w-[36rem] h-[36rem] -translate-x-1/2 rounded-full blur-[140px] opacity-60 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.18) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] translate-x-1/4 translate-y-1/4 rounded-full blur-[140px] opacity-50 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(129,140,248,0.15) 0%, transparent 70%)' }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.35),transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 mix-blend-soft-light opacity-20 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
      </div>

      {/* Content */}
      <motion.div
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          variants={itemVariants}
        >
          <motion.h1
            className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent"
            variants={itemVariants}
          >
            Music
          </motion.h1>
          
          <motion.div
            className="max-w-4xl mx-auto text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed"
            variants={itemVariants}
          >
            <p className="mb-6">
              Welcome to <span className="text-sky-400 font-semibold">PushpaSangam</span> — an attempt to blossom and connect; connect and blossom.
            </p>
            <p className="mb-6">
              First inscribed in the year 2014, PushpaSangam lives the philosophy that music making is mysticism – a medium to connect a jiva-atma, ego self, to the Brahma-Atma, the supreme collective self.
            </p>
            <p>
              Explore the musical journey through these curated playlists and compositions.
            </p>
          </motion.div>
        </motion.div>

        {/* Embeds Section */}
        <motion.div
          className="space-y-12"
          variants={itemVariants}
        >
          {/* Spotify Artist Embed */}
          <motion.div
            className="bg-black/30 backdrop-blur-xl rounded-3xl border border-white/10 p-6 sm:p-8"
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 20px 40px rgba(56, 189, 248, 0.2)"
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-4">
              <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-2">Artist Profile</h2>
              <p className="text-gray-400">Listen to the complete discography on Spotify</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                data-testid="embed-iframe"
                style={{ borderRadius: '12px' }}
                src="https://open.spotify.com/embed/artist/2wkgc34vIaza0REYBm8hcG?utm_source=generator&theme=0"
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="w-full"
              />
            </div>
          </motion.div>

          {/* YouTube Video Embed */}
          <motion.div
            className="bg-black/30 backdrop-blur-xl rounded-3xl border border-white/10 p-6 sm:p-8"
            variants={itemVariants}
            whileHover={{ 
              scale: 1.02,
              boxShadow: "0 20px 40px rgba(56, 189, 248, 0.2)"
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-4">
              <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-2">Featured Performance</h2>
              <p className="text-gray-400">Experience the visual embodiment of the music</p>
            </div>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/0_Mc5n8un6k?si=juj1Z_YhOTBcqtap"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </motion.div>

        </motion.div>
      </motion.div>
    </div>
  );
};
